import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import IconButton from '../components/IconButton';

export default function InvoicesPage() {
  const { id: clientId } = useParams();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [clientName, setClientName] = useState(location.state?.clientName || '');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await api.get('/invoices', { params: { client_id: clientId } });
        setInvoices(res.data || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch invoices');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [clientId]);

  // If client name wasn't passed in location.state, try to fetch it
  useEffect(() => {
    if (clientName || !clientId) return;
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/clients/${clientId}`);
        const data = res.data?.data || res.data;
        if (mounted) setClientName(data?.name || data?.company || '');
      } catch (e) {
        // ignore — header will fallback to id
      }
    })();
    return () => { mounted = false };
  }, [clientId, clientName]);

  const downloadInvoicePdf = async (invoice) => {
    try {
      const res = await api.get(`/invoices/${invoice.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${invoice.invoice_number || `invoice-${invoice.id}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'No PDF available for this invoice');
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number || invoice.id}? This will also remove its task associations.`)) {
      return;
    }
    try {
      setDeletingId(invoice.id);
      await api.delete(`/invoices/${invoice.id}`);
      setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSuccess(`Invoice ${invoice.invoice_number || invoice.id} deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div>Loading invoices...</div>;
  if (error) return <div style={{ color: '#de350b' }}>{error}</div>;

  return (
    <div className="clients-container">
      {success && (
        <div style={{ background: '#E3FCEF', color: '#006644', padding: '12px 16px', borderRadius: 3, marginBottom: 20, textAlign: 'center' }}>
          {success}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      {/* get the client name from the state or fetched value */}
      <h2>Invoices  - {clientName || clientId}</h2>
        {/* <div>
          <button onClick={() => navigate(-1)} className="back-btn">Back</button>
        </div> */}
      </div>

      

      <table className="clients-table">
        <thead>
          <tr>
            <th>Invoice #</th>
            <th>Invoice Date</th>
            <th>Created At</th>
            <th>Task Count</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map(inv => (
            <tr key={inv.id}>
              <td>{inv.invoice_number}</td>
              <td>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}</td>
              <td>{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}</td>
              {/* provide task ids with hyperlinks */}
              <td>
                {inv.invoiceTasks && inv.invoiceTasks.length > 0 ? (
                  <ul style={{ margin: 0, paddingInlineStart: 16 }}>
                    {inv.invoiceTasks.map(task => (
                      <li key={task.id}>
                        <a href={`/tasks/edit/${task.task_id}`} target="_blank" rel="noopener noreferrer">
                          {task.id}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  0
                )}
              </td>
              {/* <td>{(inv.invoiceTasks && inv.invoiceTasks.length) || 0}</td> */}
              <td>
                <div className="action-buttons">
                  <IconButton title="View" onClick={() => alert(JSON.stringify(inv, null, 2))}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                  <IconButton title="Download PDF" onClick={() => downloadInvoicePdf(inv)}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4V4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 2v4M12 2v4M16 2v4M10 14l2 2 2-2M12 4v10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                  <IconButton title="Delete invoice" onClick={() => handleDelete(inv)} disabled={deletingId === inv.id}>
                    <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
